import React, { useEffect, useRef, useState } from "react";
import { QRCodeSVG, QRCodeCanvas } from "qrcode.react";
import OwnerLayout from "@/components/OwnerLayout";
import { api, fileUrl } from "@/lib/api";
import { Plus, Trash2, Printer, Download, ExternalLink, QrCode, X } from "lucide-react";
import { toast } from "sonner";

export default function QRCodesPage() {
  const [tables, setTables] = useState([]);
  const [settings, setSettings] = useState(null);
  const [newTable, setNewTable] = useState("");
  const [bulkCount, setBulkCount] = useState(5);
  const [preview, setPreview] = useState(null); // table string
  const canvasRefs = useRef({});

  const origin = typeof window !== "undefined" ? window.location.origin : "";

  const load = async () => {
    const { data } = await api.get("/settings");
    setSettings(data);
    setTables(data.tables || []);
  };
  useEffect(() => { load(); }, []);

  const save = async (nextTables) => {
    await api.put("/settings", { tables: nextTables });
    setTables(nextTables);
    toast.success("Meja diperbarui");
  };

  const addOne = async () => {
    const t = newTable.trim();
    if (!t) return;
    if (tables.includes(t)) return toast.error("Meja sudah ada");
    await save([...tables, t]);
    setNewTable("");
  };

  const addBulk = async () => {
    const n = Math.max(1, Math.min(50, Number(bulkCount) || 0));
    const start = tables.length > 0 ? Math.max(...tables.map((t) => parseInt(t, 10) || 0)) + 1 : 1;
    const additions = [];
    for (let i = 0; i < n; i++) {
      const next = String(start + i);
      if (!tables.includes(next)) additions.push(next);
    }
    if (additions.length === 0) return toast.error("Tidak ada meja baru untuk ditambahkan");
    await save([...tables, ...additions]);
  };

  const removeTable = async (t) => {
    if (!window.confirm(`Hapus meja "${t}"?`)) return;
    await save(tables.filter((x) => x !== t));
  };

  const linkFor = (t) => `${origin}/?table=${encodeURIComponent(t)}`;

  const downloadOne = (t) => {
    const canvas = canvasRefs.current[t];
    if (!canvas) return;
    const url = canvas.toDataURL("image/png");
    const a = document.createElement("a");
    a.href = url;
    a.download = `qr-meja-${t}.png`;
    a.click();
  };

  const printAll = () => {
    window.print();
  };

  if (!settings) return <OwnerLayout><div className="text-[#5C6A61]">Memuat...</div></OwnerLayout>;

  return (
    <OwnerLayout>
      <div className="print:hidden">
        <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
          <div>
            <h1 className="font-display text-3xl font-semibold text-[#1E3A2B]">QR Code Meja</h1>
            <p className="text-[#5C6A61] mt-1">Cetak dan tempel QR di tiap meja. Pelanggan tinggal scan untuk langsung memesan.</p>
          </div>
          <div className="flex gap-2">
            <button
              data-testid="btn-print-all-qr"
              onClick={printAll}
              className="bg-[#1E3A2B] text-white px-4 py-2.5 rounded-xl text-sm font-medium inline-flex items-center gap-2"
            >
              <Printer className="w-4 h-4" /> Cetak Semua
            </button>
          </div>
        </div>

        <div className="bg-white rounded-md border border-[#D3CEC4] p-5 mb-6">
          <h2 className="font-display text-base font-semibold text-[#1E3A2B] mb-3">Kelola Meja</h2>
          <div className="flex flex-wrap gap-3 items-end">
            <div>
              <label className="text-xs uppercase tracking-widest font-bold text-[#5C6A61] block mb-1">Tambah Meja</label>
              <div className="flex gap-2">
                <input
                  data-testid="input-new-table"
                  value={newTable}
                  onChange={(e) => setNewTable(e.target.value)}
                  placeholder="Contoh: VIP-1, Teras, 12"
                  className="border border-[#D3CEC4] rounded-xl px-3 py-2 text-sm w-48"
                />
                <button data-testid="btn-add-table" onClick={addOne} className="bg-[#C25934] text-white px-3 py-2 rounded-xl text-sm inline-flex items-center gap-1">
                  <Plus className="w-4 h-4" /> Tambah
                </button>
              </div>
            </div>
            <div>
              <label className="text-xs uppercase tracking-widest font-bold text-[#5C6A61] block mb-1">Tambah Beberapa Meja</label>
              <div className="flex gap-2">
                <input
                  data-testid="input-bulk-count"
                  type="number"
                  value={bulkCount}
                  onChange={(e) => setBulkCount(e.target.value)}
                  min={1}
                  max={50}
                  className="border border-[#D3CEC4] rounded-xl px-3 py-2 text-sm w-24"
                />
                <button data-testid="btn-add-bulk" onClick={addBulk} className="bg-[#EAE7DF] text-[#1E3A2B] px-3 py-2 rounded-xl text-sm">
                  Tambah {bulkCount || 0} meja berurutan
                </button>
              </div>
            </div>
          </div>
          <div className="mt-3 text-xs text-[#5C6A61]">Total: <span className="font-semibold text-[#1E3A2B]">{tables.length} meja</span> · Boleh pakai angka atau nama kreatif seperti "VIP-1", "Teras", "Lantai 2 #4"</div>
        </div>

        {tables.length === 0 ? (
          <div className="text-center py-16 text-[#5C6A61] border border-dashed border-[#D3CEC4] rounded-md">
            Belum ada meja. Tambahkan meja terlebih dahulu.
          </div>
        ) : (
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4" data-testid="qr-grid">
            {tables.map((t) => (
              <div key={t} className="bg-white rounded-md border border-[#D3CEC4] p-4 flex flex-col items-center text-center" data-testid={`qr-card-${t}`}>
                <div className="w-full flex items-center justify-between mb-2">
                  <span className="text-xs uppercase tracking-widest font-bold text-[#5C6A61]">Meja</span>
                  <button data-testid={`btn-remove-table-${t}`} onClick={() => removeTable(t)} className="p-1 text-[#D64045] hover:bg-[#F9F9F6] rounded"><Trash2 className="w-3.5 h-3.5" /></button>
                </div>
                <div className="font-display text-3xl font-semibold text-[#1E3A2B] mb-2">{t}</div>
                <div className="p-2 bg-white border border-[#EAE7DF] rounded-md">
                  <QRCodeCanvas
                    value={linkFor(t)}
                    size={140}
                    level="M"
                    includeMargin={false}
                    ref={(el) => { if (el) canvasRefs.current[t] = el; }}
                  />
                </div>
                <div className="flex gap-1 mt-3 w-full">
                  <button data-testid={`btn-preview-qr-${t}`} onClick={() => setPreview(t)} className="flex-1 bg-[#EAE7DF] text-[#1E3A2B] px-2 py-1.5 rounded-md text-xs">Preview</button>
                  <button data-testid={`btn-download-qr-${t}`} onClick={() => downloadOne(t)} className="flex-1 bg-[#1E3A2B] text-white px-2 py-1.5 rounded-md text-xs inline-flex items-center justify-center gap-1">
                    <Download className="w-3 h-3" /> PNG
                  </button>
                </div>
                <a href={linkFor(t)} target="_blank" rel="noreferrer" className="text-[10px] text-[#5C6A61] mt-2 truncate w-full inline-flex items-center justify-center gap-1 hover:text-[#C25934]">
                  <ExternalLink className="w-3 h-3" /> Buka
                </a>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Print layout: hidden on screen, visible on print */}
      <div className="hidden print:block">
        <style>{`
          @media print {
            body { background: white !important; }
            .no-print { display: none !important; }
            .print-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 12mm; padding: 10mm; }
            .print-card { break-inside: avoid; border: 1px dashed #999; padding: 10mm; text-align: center; }
          }
        `}</style>
        <div className="print-grid">
          {tables.map((t) => (
            <div key={t} className="print-card">
              {settings.logo_path && (
                <img src={fileUrl(settings.logo_path)} alt="logo" style={{ width: 50, height: 50, objectFit: "cover", borderRadius: 8, margin: "0 auto 6px" }} />
              )}
              <div style={{ fontSize: 14, fontWeight: 600 }}>{settings.restaurant_name || "Warung"}</div>
              <div style={{ fontSize: 11, color: "#555", marginBottom: 10 }}>Scan untuk Memesan</div>
              <QRCodeSVG value={linkFor(t)} size={220} level="M" includeMargin />
              <div style={{ fontSize: 24, fontWeight: 700, marginTop: 10 }}>Meja {t}</div>
            </div>
          ))}
        </div>
      </div>

      {preview && (
        <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4" onClick={() => setPreview(null)}>
          <div className="bg-white rounded-md p-6 max-w-sm w-full text-center" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-3">
              <div className="font-display font-semibold text-[#1E3A2B]">QR Meja {preview}</div>
              <button onClick={() => setPreview(null)}><X className="w-5 h-5 text-[#5C6A61]" /></button>
            </div>
            {settings.logo_path && <img src={fileUrl(settings.logo_path)} alt="logo" className="w-14 h-14 object-cover rounded-lg mx-auto mb-2" />}
            <div className="text-sm font-semibold text-[#1E3A2B]">{settings.restaurant_name || "Warung"}</div>
            <div className="text-xs text-[#5C6A61] mb-4">Scan untuk Memesan</div>
            <div className="inline-block p-3 bg-white border border-[#EAE7DF] rounded-md">
              <QRCodeSVG value={linkFor(preview)} size={240} level="M" includeMargin />
            </div>
            <div className="font-display text-2xl font-semibold mt-3 text-[#1E3A2B]">Meja {preview}</div>
            <div className="text-xs text-[#5C6A61] mt-2 break-all">{linkFor(preview)}</div>
          </div>
        </div>
      )}
    </OwnerLayout>
  );
}
